/**
 * Copyright IBM Corp. 2022, 2026
 */

import * as React from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { AnimatePresence, m } from 'motion/react'
import s from './style.module.css'

export interface IoDialogProps {
	isOpen: boolean
	onDismiss: () => void
	label: string
	children: React.ReactNode
}

const AnimatedDialogOverlay = m(Dialog.Overlay)

export default function IoDialog({
	isOpen,
	onDismiss,
	children,
	label,
}: IoDialogProps) {
	return (
		<AnimatePresence>
			{isOpen && (
				<Dialog.Root
					open={isOpen}
					onOpenChange={(isOpen) => {
						!isOpen && onDismiss()
					}}
				>
					<Dialog.Portal>
						<AnimatedDialogOverlay
							className={s.dialogOverlay}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
						/>
						<Dialog.Content className={s.dialogWrapper}>
							<m.div
								initial={{ y: 50, opacity: 0 }}
								animate={{ y: 0, opacity: 1 }}
								exit={{ y: 50, opacity: 0 }}
								transition={{ min: 0, max: 100, bounceDamping: 8 }}
								style={{ width: '100%', maxWidth: 800 }}
							>
								<div className={s.dialogContent}>
									<VisuallyHidden asChild>{label}</VisuallyHidden>
									<button onClick={onDismiss} className={s.dialogClose}>
										Close
									</button>
									{children}
								</div>
							</m.div>
						</Dialog.Content>
					</Dialog.Portal>
				</Dialog.Root>
			)}
		</AnimatePresence>
	)
}
